import { useCallback, useEffect, useRef, useState } from 'react';
import { forceReloadFresh } from '../../utils/clientCache';

let APP_VERSION = 'dev';
let BUILD_HASH = null;
try {
  const m = require('../../appVersion');
  APP_VERSION = m.APP_VERSION || 'dev';
  BUILD_HASH = m.BUILD_HASH || null;
} catch (e) { /* dev sem appVersion.js gerado */ }

const POLL_INTERVAL_MS = 60_000;
const FIRST_CHECK_DELAY_MS = 15_000;

const isDevEnvironment = () => {
  if (process.env.NODE_ENV === 'development') return true;
  if (APP_VERSION === 'dev' || !BUILD_HASH) return true;
  return false;
};

// Comparação semver simples (major.minor.patch). Retorna 1, -1, 0.
// Trata partes não numéricas como 0 e versões com mais segmentos como maior.
const compareSemver = (a, b) => {
  const pa = String(a || '0').split('.').map(n => parseInt(n, 10) || 0);
  const pb = String(b || '0').split('.').map(n => parseInt(n, 10) || 0);
  const len = Math.max(pa.length, pb.length, 3);
  for (let i = 0; i < len; i++) {
    const x = pa[i] || 0;
    const y = pb[i] || 0;
    if (x > y) return 1;
    if (x < y) return -1;
  }
  return 0;
};

const fetchServerVersion = async () => {
  const url = `/version.json?_=${Date.now()}`;
  const res = await fetch(url, { cache: 'no-store', credentials: 'omit' });
  if (!res.ok) throw new Error(`version.json HTTP ${res.status}`);
  return res.json();
};

// Toda a lógica de "limpar e recarregar" foi centralizada em utils/clientCache.
// Mantemos um wrapper aqui para compatibilidade com o resto do hook.
const performHardReload = () => forceReloadFresh();

export default function useAppVersionCheck() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [latestVersion, setLatestVersion] = useState(null);
  const inFlightRef = useRef(false);

  const check = useCallback(async () => {
    if (isDevEnvironment()) return;
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      const server = await fetchServerVersion();
      if (!server) return;

      const serverHash = server.buildHash || null;
      const serverVersion = server.version || null;
      if (!serverHash && !serverVersion) return;

      const cmp = compareSemver(serverVersion, APP_VERSION);

      // Bloqueia downgrade: se o servidor reporta versão MENOR que a rodando,
      // ignora silenciosamente. Cenário típico: deploy parcial, version.json
      // cacheado por proxy, ou bundle local mais novo que o servidor de prod.
      // Sugerir "atualizar" para uma versão menor causaria downgrade automático
      // ao clicar no Snackbar.
      if (cmp < 0) {
        if (process.env.NODE_ENV !== 'production') {
          console.warn(
            `[useAppVersionCheck] Servidor retornou versão menor (${serverVersion}) ` +
            `que a atual (${APP_VERSION}). Update suprimido para evitar downgrade.`
          );
        }
        return;
      }

      const versionAhead = cmp > 0;
      const hashChanged = serverHash && BUILD_HASH && serverHash !== BUILD_HASH;
      const sameVersionDifferentBuild = cmp === 0 && hashChanged;

      // Só sugere update se for genuinamente uma versão nova OU rebuild da mesma versão.
      if (versionAhead || sameVersionDifferentBuild) {
        setLatestVersion(serverVersion);
        setUpdateAvailable(true);
      } else {
        // Cliente já está sincronizado: garante que o estado reflita isso
        // (caso um trigger anterior tenha marcado update por engano).
        setUpdateAvailable(false);
      }
    } catch (e) {
      // Silencioso: rede instável não deve poluir console
    } finally {
      inFlightRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (isDevEnvironment()) return;

    const firstTimer = setTimeout(check, FIRST_CHECK_DELAY_MS);
    const interval = setInterval(check, POLL_INTERVAL_MS);

    // Evento custom emitido por api.js (response header x-app-version) e useSocket
    // (evento "app_version" do servidor). NÃO marca updateAvailable diretamente —
    // delega para check() que faz a comparação semver e bloqueia downgrade.
    const onMismatch = () => { check(); };
    window.addEventListener('app:version-mismatch', onMismatch);

    const onVisibility = () => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', onVisibility);

    const onFocus = () => check();
    window.addEventListener('focus', onFocus);

    return () => {
      clearTimeout(firstTimer);
      clearInterval(interval);
      window.removeEventListener('app:version-mismatch', onMismatch);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
    };
  }, [check]);

  const performUpdate = useCallback(() => {
    performHardReload();
  }, []);

  return {
    updateAvailable,
    currentVersion: APP_VERSION,
    currentHash: BUILD_HASH,
    latestVersion,
    performUpdate,
    checkNow: check
  };
}
